import { countries as countryList, getCountryNameFromCode } from './countryUtils';

export const DEFAULT_SHIPPING_RATES = {
  small: 1000,
  medium: 2000,
  large: 3000,
};

export const DEFAULT_QUIKBAK_FEE = 1500;

const isoByName = new Map(
  countryList.map(({ name, isoCode }) => [name.trim().toLowerCase(), isoCode.trim().toUpperCase()])
);

const nameByIso = new Map(
  countryList.map(({ name, isoCode }) => [isoCode.trim().toUpperCase(), name])
);

export function normalizeCountryCode(raw = '') {
  if (typeof raw !== 'string') {
    return '';
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return '';
  }

  const upper = trimmed.toUpperCase();
  if (upper.length === 2) {
    return upper;
  }

  const mappedIso = isoByName.get(trimmed.toLowerCase());
  if (mappedIso) {
    return mappedIso;
  }

  return trimmed;
}

function buildCountryCandidates(input = '') {
  if (typeof input !== 'string') {
    return [];
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return [];
  }

  const lower = trimmed.toLowerCase();
  const candidates = new Set([lower]);

  if (lower.length === 2) {
    const displayName = nameByIso.get(trimmed.toUpperCase()) || getCountryNameFromCode(trimmed);
    if (displayName) {
      candidates.add(displayName.toLowerCase());
    }
  } else {
    const possibleIso = isoByName.get(lower);
    if (possibleIso) {
      candidates.add(possibleIso.toLowerCase());
    }
  }

  return Array.from(candidates);
}

export function normalizeShippingSettings(raw = {}) {
  const countries = Object.entries(raw.countries ?? {}).reduce((acc, [countryKey, rates]) => {
    const normalizedCode = normalizeCountryCode(countryKey);
    if (!normalizedCode) {
      return acc;
    }

    const sanitizedRates = typeof rates === 'object' && rates !== null ? rates : {};
    acc[normalizedCode] = sanitizedRates;
    return acc;
  }, {});
  const defaultRates = raw.defaultRates ?? DEFAULT_SHIPPING_RATES;
  const quikbakFee = typeof raw.quikbakFee === 'number' ? Math.round(raw.quikbakFee) : DEFAULT_QUIKBAK_FEE;
  return { countries, defaultRates, quikbakFee };
}

export function computeShippingCost(settings, country, packageSize) {
  const { countries, defaultRates } = normalizeShippingSettings(settings);

  const countryCandidates = buildCountryCandidates(country);
  const normalizedSize = (packageSize || '').trim().toLowerCase();

  if (!normalizedSize) {
    return 0;
  }

  const selectRate = (rates) => {
    if (!rates) return undefined;
    for (const [key, value] of Object.entries(rates)) {
      if (key.trim().toLowerCase() === normalizedSize && typeof value === 'number' && value > 0) {
        return Math.round(value);
      }
    }
    return undefined;
  };

  let countryRates;
  for (const [key, value] of Object.entries(countries)) {
    const normalizedKey = String(key).trim().toLowerCase();
    if (countryCandidates.includes(normalizedKey)) {
      countryRates = value;
      break;
    }
  }

  const countryRate = selectRate(countryRates);
  if (typeof countryRate === 'number') {
    return countryRate;
  }

  const defaultRate = selectRate(defaultRates);
  if (typeof defaultRate === 'number') {
    return defaultRate;
  }

  return 0;
}
